import { useState, useEffect } from 'react';
import { useAuthState } from '@/hooks/useAuthState';
import { supabase } from '@/lib/supabase';
import { Place } from '@/types';

interface SavedBenchmarkComparisonData {
  selectedPlace: Place | null;
  peerPlaces: Place[];
  activeTab: string;
  metricsData?: Record<string, unknown>;
}

interface SavedBenchmarkComparisonResult {
  data: SavedBenchmarkComparisonData | null;
  loading: boolean;
  error: string | null;
}

export const useSavedBenchmarkComparison = (): SavedBenchmarkComparisonResult => {
  const { user } = useAuthState();
  const [data, setData] = useState<SavedBenchmarkComparisonData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSavedComparison = async () => {
      // If not authenticated, don't attempt to load
      if (!user) {
        setData(null);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Get the most recent comparison for this user
        const { data: comparisons, error: fetchError } = await supabase
          .from('benchmark_comparisons')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1);

        if (fetchError) {
          throw fetchError;
        }

        if (!comparisons || comparisons.length === 0) {
          setData(null);
          return;
        }

        const savedComparison = comparisons[0];
        setData({
          selectedPlace: savedComparison.selected_place,
          peerPlaces: savedComparison.peer_places,
          activeTab: savedComparison.active_tab,
          metricsData: savedComparison.metrics_data,
        });
      } catch (err) {
        console.error('Error loading saved comparison:', err);
        setError(err instanceof Error ? err.message : 'Failed to load saved comparison');
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    loadSavedComparison();
  }, [user]);

  return { data, loading, error };
}; 